import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TagResponse } from '../types';
import { Chip } from './Chip';

interface Props {
  allTags: TagResponse[];
  /** Trigger element the popover is positioned against. */
  anchorEl: HTMLElement | null;
  excludeNames?: string[];
  onSelect: (name: string) => void;
  onClose: () => void;
  placeholder?: string;
}

const WIDTH = 256; // w-64
const GAP = 4;
const MARGIN = 8; // keep clear of the viewport edges
const MIN_HEIGHT = 160;

interface Placement {
  left: number;
  top: number;
  maxHeight: number;
}

function computePlacement(anchor: HTMLElement): Placement {
  const rect = anchor.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - GAP - MARGIN;
  const above = rect.top - GAP - MARGIN;
  // Flip above the trigger when there is more room there and below is cramped.
  const flip = below < MIN_HEIGHT && above > below;
  // Aim for MIN_HEIGHT even when the chosen side is cramped, but never exceed the
  // viewport — otherwise the clamp below could not keep the whole menu on screen.
  const maxHeight = Math.min(
    Math.max(MIN_HEIGHT, flip ? above : below),
    Math.max(0, window.innerHeight - 2 * MARGIN),
  );
  const left = Math.min(
    Math.max(MARGIN, rect.left),
    Math.max(MARGIN, window.innerWidth - WIDTH - MARGIN),
  );
  const top = flip ? rect.top - GAP - maxHeight : rect.bottom + GAP;
  return {
    left,
    // Neither side had room for MIN_HEIGHT: overlap the trigger rather than
    // hang off the viewport edge, which is what made the menu unreachable.
    top: Math.min(
      Math.max(MARGIN, top),
      Math.max(MARGIN, window.innerHeight - maxHeight - MARGIN),
    ),
    maxHeight,
  };
}

export function TagPopover({
  allTags, anchorEl, excludeNames = [], onSelect, onClose, placeholder = 'Tag name…',
}: Props) {
  const [query, setQuery] = useState('');
  const [placement, setPlacement] = useState<Placement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const needle = query.trim().toLowerCase();
  const excluded = new Set(excludeNames);
  const matches = allTags
    .filter(t => !excluded.has(t.name))
    .filter(t => needle === '' || t.name.toLowerCase().includes(needle))
    .slice(0, 12);
  const showCreate =
    needle.length > 0 &&
    !allTags.some(t => t.name === needle) &&
    !excluded.has(needle);

  // Position against the trigger, and keep it there while the page scrolls/resizes.
  useLayoutEffect(() => {
    if (!anchorEl) return;
    const reposition = () => setPlacement(computePlacement(anchorEl));
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [anchorEl]);

  // Close on click outside (the trigger itself toggles, so exclude it).
  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as globalThis.Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [anchorEl, onClose]);

  function submit(name: string) {
    const trimmed = name.trim().toLowerCase();
    if (trimmed.length === 0) return;
    onSelect(trimmed);
    setQuery('');
  }

  if (!placement) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-50 flex flex-col bg-card border border-border rounded shadow-lg w-64 p-2"
      style={{ left: placement.left, top: placement.top, maxHeight: placement.maxHeight }}
      onClick={e => e.stopPropagation()}
      // On the container, not the input: Escape must work with focus on a tag button too.
      onKeyDown={e => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <input
        autoFocus
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit(query);
          }
        }}
        placeholder={placeholder}
        className="w-full border border-input rounded px-2 py-1 text-sm bg-background mb-2 shrink-0"
      />
      <div className="min-h-0 overflow-y-auto flex flex-col gap-1">
        {matches.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => submit(t.name)}
            className="text-left px-1 py-0.5 rounded hover:bg-muted flex items-center justify-between"
          >
            <Chip label={t.name} color={t.color} />
            <span className="text-xs text-muted-foreground">{t.flowCount}</span>
          </button>
        ))}
        {matches.length === 0 && !showCreate && (
          <p className="text-xs text-muted-foreground px-1 py-1">No matching tags</p>
        )}
        {showCreate && (
          <button
            type="button"
            onClick={() => submit(needle)}
            className="text-left px-2 py-1 rounded hover:bg-primary/10 text-sm text-primary"
          >
            + Create „{needle}"
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
